import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  BooleanField,
  TopToolbar,
  useRecordContext,
  useUpdate,
  useNotify,
  useRefresh,
  Button,
} from 'react-admin';
import {
  NotificationType,
  NotificationPriority,
} from '../../generated/data-contracts';
import { Chip, Box, Typography } from '@mui/material';
import { MarkEmailRead } from '@mui/icons-material';

// Componente para renderizar el tipo con colores
const TypeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const typeColors = {
    [NotificationType.Info]: 'info',
    [NotificationType.Warning]: 'warning',
    [NotificationType.Alert]: 'error',
    [NotificationType.Success]: 'success',
  } as const;

  const typeLabels = {
    [NotificationType.Info]: 'Información',
    [NotificationType.Warning]: 'Advertencia',
    [NotificationType.Alert]: 'Alerta',
    [NotificationType.Success]: 'Éxito',
  };

  return (
    <Chip
      label={typeLabels[record.type as NotificationType] || record.type}
      color={typeColors[record.type as NotificationType] || 'default'}
      size='medium'
    />
  );
};

// Componente para renderizar la prioridad con colores
const PriorityField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const priorityColors = {
    [NotificationPriority.Low]: 'default',
    [NotificationPriority.Medium]: 'primary',
    [NotificationPriority.High]: 'warning',
    [NotificationPriority.Critical]: 'error',
  } as const;

  const priorityLabels = {
    [NotificationPriority.Low]: 'Baja',
    [NotificationPriority.Medium]: 'Media',
    [NotificationPriority.High]: 'Alta',
    [NotificationPriority.Critical]: 'Crítica',
  };

  return (
    <Chip
      label={
        priorityLabels[record.priority as NotificationPriority] ||
        record.priority
      }
      color={
        priorityColors[record.priority as NotificationPriority] || 'default'
      }
      size='medium'
    />
  );
};

// Componente para las acciones en el show
const NotificationShowActions = () => {
  const record = useRecordContext();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!record || record.isRead) return null;

  const handleMarkAsRead = async () => {
    try {
      await update(
        'notifications',
        {
          id: record.id,
          data: {},
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            notify('Notificación marcada como leída', { type: 'success' });
            refresh();
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch (error: any) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <TopToolbar>
      <Button
        onClick={handleMarkAsRead}
        label='Marcar como leída'
        startIcon={<MarkEmailRead />}
      />
    </TopToolbar>
  );
};

export const NotificationsShow: React.FC = () => (
  <Show
    actions={<NotificationShowActions />}
    title='Detalles de la Notificación'
  >
    <SimpleShowLayout>
      <Box display='flex' flexDirection='column' gap={2}>
        <Box>
          <Typography variant='h6' gutterBottom>
            Información General
          </Typography>
          <TextField source='id' label='ID' />
          <TextField source='title' label='Título' />
          <Box display='flex' gap={2} alignItems='center' my={1}>
            <Box>
              <Typography variant='body2' color='textSecondary'>
                Tipo:
              </Typography>
              <TypeField />
            </Box>
            <Box>
              <Typography variant='body2' color='textSecondary'>
                Prioridad:
              </Typography>
              <PriorityField />
            </Box>
          </Box>
          <BooleanField source='isRead' label='Leída' />
        </Box>

        <Box>
          <Typography variant='h6' gutterBottom>
            Contenido
          </Typography>
          <TextField source='content' label='Contenido' />
        </Box>

        <Box>
          <Typography variant='h6' gutterBottom>
            Fechas
          </Typography>
          <DateField source='createdAt' label='Fecha de creación' showTime />
          <DateField source='expiresAt' label='Fecha de expiración' showTime />
          <DateField source='readAt' label='Fecha de lectura' showTime />
        </Box>
      </Box>
    </SimpleShowLayout>
  </Show>
);
